<template>
  <div class="space-y-4">
    <!-- The list of drafts: shown until one is opened. -->
    <div v-if="!current" class="rounded-lg border border-gray-200 bg-white" data-test="drafts-list">
      <div class="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3">
        <div>
          <h3 class="font-semibold text-gray-900">✨ Edit with AI</h3>
          <p class="mt-1 text-gray-600">Change a draft of this document by asking the AI or by hand, and try it on an engagement. Users keep getting the live version until you publish.</p>
        </div>
        <button type="button" :class="[PRIMARY_BUTTON, 'ml-auto']" data-test="new-draft" @click="startNew">New draft</button>
      </div>
      <div v-if="error" role="alert" class="mx-4 mt-3 rounded-md bg-red-tint px-3 py-2 text-gray-900">{{ error }}</div>
      <p v-if="loading" class="px-4 py-6 text-gray-500" data-test="drafts-loading">Loading drafts…</p>
      <p v-else-if="!drafts.length" class="px-4 py-6 text-gray-600" data-test="drafts-intro">No drafts yet. Start one to change this document without affecting anyone.</p>
      <table v-else class="w-full">
        <thead>
          <tr class="text-left text-[11px] font-medium uppercase tracking-wider text-gray-500">
            <th class="px-4 py-2">Name</th>
            <th class="px-4 py-2">Started from</th>
            <th class="px-4 py-2">By</th>
            <th class="px-4 py-2">Last changed</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-for="d in drafts" :key="d.id" class="cursor-pointer hover:bg-gray-50" :data-test="`draft-row-${d.id}`" @click="open(d)">
            <td class="px-4 py-3"><button type="button" :class="TEXT_BUTTON" :data-test="`open-draft-${d.id}`" @click.stop="open(d)">{{ d.name }}</button></td>
            <td class="px-4 py-3 text-gray-700">
              Version {{ d.base_version }}
              <span v-if="d.base_version !== template.version" class="ml-2 rounded bg-neutral-100 px-2 text-gray-900">Live is now version {{ template.version }}</span>
            </td>
            <td class="px-4 py-3 text-gray-700"><span v-if="d.created_by_name" class="sensitive" data-spoof-kind="person">{{ d.created_by_name }}</span></td>
            <td class="px-4 py-3 text-gray-500" :title="absolute(d.updated_at)">{{ relative(d.updated_at) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- The open draft: whether it's saved, and Publish. -->
    <div v-if="current" class="rounded-lg border border-gray-200 bg-white px-4 py-3" data-test="drafts-bar">
      <div class="flex flex-wrap items-center gap-3">
        <button type="button" :class="TEXT_BUTTON" data-test="all-drafts" @click="backToList">← All drafts</button>
        <span class="font-semibold text-gray-900" data-test="draft-name">{{ current.name }}</span>
        <span class="text-gray-500" data-test="save-status">{{ saveStatus }}</span>
        <div class="ml-auto flex items-center gap-3">
          <button type="button" :class="TEXT_BUTTON" :disabled="busy || !baseVersion || !hasChanges" data-test="discard-changes" @click="confirmDiscard = true">Discard changes</button>
          <button type="button" :class="PRIMARY_BUTTON" :disabled="busy || dirty || saving || !!jsonError" data-test="promote-draft" @click="openPromote">Publish…</button>
        </div>
      </div>

      <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-600" data-test="lineage">
        <span>Started from version {{ current.base_version }}</span>
        <button type="button" :class="TEXT_BUTTON" :disabled="!baseVersion" data-test="diff-mine" @click="showDiff('mine')">See all my changes</button>
        <template v-if="liveMoved">
          <span class="rounded bg-neutral-100 px-2 text-gray-900">Live has changed since (now version {{ template.version }})</span>
          <button type="button" :class="TEXT_BUTTON" :disabled="!baseVersion" data-test="diff-live" @click="showDiff('live')">See what changed in live</button>
        </template>
      </div>
      <div v-if="confirmDiscard" class="mt-3 flex items-center gap-3 rounded-md bg-neutral-50 px-4 py-3 text-gray-900" data-test="discard-confirm">
        <span>Discard all changes in this draft? It goes back to version {{ current.base_version }}, the version it started from. The draft stays.</span>
        <button type="button" :class="[TEXT_BUTTON, 'ml-auto']" @click="confirmDiscard = false">Keep my changes</button>
        <button type="button" :class="PRIMARY_BUTTON" data-test="confirm-discard" @click="discardChanges">Discard changes</button>
      </div>
      <div v-if="error" role="alert" class="mt-3 rounded-md bg-red-tint px-3 py-2 text-gray-900">{{ error }}</div>
    </div>

    <template v-if="current">
      <div class="grid items-start gap-4 lg:grid-cols-5">
        <!-- Left: ask the AI. As tall as its conversation, and it stays in view while the document scrolls. -->
        <div class="flex flex-col rounded-lg border border-gray-200 bg-white p-4 lg:sticky lg:top-4 lg:col-span-2 lg:max-h-[calc(100vh-2rem)]" data-test="ask-ai-panel">
          <h3 class="mb-2 flex-none font-semibold text-gray-900">Ask AI</h3>
          <HermesAuthoringAssistant
            :draft-id="current.id" :draft="draftNow" :dirty="dirty || saving" :title="template.name"
            @apply="replaceDraft" @undo="replaceDraft"
          />
        </div>

        <!-- Right: the document. -->
        <div class="rounded-lg border border-gray-200 bg-white p-4 lg:col-span-3">
          <nav class="mb-3 flex gap-6 border-b border-gray-200">
            <button
              v-for="t in VIEW_TABS" :key="t.key" type="button" :data-test="`view-${t.key}`"
              :class="['-mb-px border-b-2 py-2 font-medium', view === t.key ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900']"
              @click="view = t.key"
            >{{ t.label }}</button>
          </nav>

          <div v-if="view === 'document'" data-test="document-view">
            <p class="mb-3 text-gray-500">How the document looks with this draft. The text is placeholder until you try it on an engagement.</p>
            <div class="rounded-md border border-gray-200 bg-gray-50 p-4">
              <SummaryDocumentPage :title="template.name" :sections="layout.sections" :content="layout.content" />
            </div>
          </div>

          <div v-else-if="view === 'instructions'" data-test="instructions-view">
            <div class="mb-3 flex items-start gap-3">
              <p class="flex-1 text-gray-500">The general rules the AI follows when it writes this document. Rules for one section live with that section.</p>
              <button type="button" :class="SECONDARY_BUTTON" :data-test="promptView === 'read' ? 'prompt-edit' : 'prompt-read'" @click="promptView = promptView === 'read' ? 'edit' : 'read'">{{ promptView === 'read' ? 'Edit' : 'Done' }}</button>
            </div>
            <div v-if="promptView === 'read'" class="prose prose-sm max-w-none rounded-md border border-gray-200 bg-gray-50 px-5 py-4" data-test="prompt-rendered" v-html="promptHtml" />
            <textarea v-else v-model="form.prompt" rows="28" :class="[INPUT, 'font-mono leading-6']" spellcheck="false" data-test="draft-prompt" />
            <div v-if="isSuperuser && promptNotes.length" class="mt-3 rounded-md bg-neutral-50 px-3 py-2" data-test="draft-prompt-lint">
              <div class="mb-1 font-medium text-gray-900">Markdown lint · {{ promptNotes.length }} note{{ promptNotes.length === 1 ? '' : 's' }}<span class="ml-2 rounded border border-gray-200 bg-white px-2 text-[11px] font-normal text-gray-500">Beta · superusers only</span></div>
              <ul class="space-y-1">
                <li v-for="(n, i) in promptNotes" :key="i" class="text-gray-700"><span class="font-mono text-gray-500">Line {{ n.line }}</span> · {{ n.message }}</li>
              </ul>
            </div>
          </div>

          <div v-else data-test="try-view">
            <p class="mb-3 text-gray-600">Runs this draft on one engagement, using that engagement's call and notes. It creates no documents and sends nothing.</p>
            <div class="flex flex-wrap items-end gap-3">
              <label class="block"><span class="mb-2 block text-gray-500">Engagement (Work id)</span><input :value="workId || ''" type="text" inputmode="numeric" placeholder="e.g. 3234179" :class="SIZED_INPUT" data-test="work-id" @input="setWorkId($event)"></label>
              <button type="button" :class="PRIMARY_BUTTON" :disabled="!workId || busy || dirty || saving || draftLive" data-test="run-draft" @click="run">{{ draftLive ? 'Running…' : 'Try it' }}</button>
            </div>
            <p v-if="draftLive" class="mt-3 text-gray-600" data-test="running-note">Running on engagement {{ draftRun.unit_key }}… it appears below when it finishes.</p>
            <div v-if="draftRuns.length" class="mt-5 border-t border-gray-200 pt-4" data-test="draft-runs">
              <div class="mb-2 font-medium text-gray-900">Tries of this draft</div>
              <table class="w-full">
                <tbody class="divide-y divide-gray-100">
                  <tr v-for="r in draftRuns" :key="r.id">
                    <td class="py-2 pr-3"><HermesBadge :value="r.status" /></td>
                    <td class="py-2 pr-3 text-gray-600">Engagement {{ r.unit_key }}</td>
                    <td class="py-2 pr-3 text-gray-500" :title="absolute(r.created_at)">{{ relative(r.created_at) }}</td>
                    <td class="whitespace-nowrap py-2 text-right">
                      <button type="button" :class="SECONDARY_BUTTON" :disabled="r.status !== 'completed'" :data-test="`show-run-${r.id}`" @click="showRun(r)">Compare with live</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Hands-on editing, for when asking isn't enough. -->
      <details class="rounded-lg border border-gray-200 bg-white" data-test="edit-sections" @toggle="onSectionsToggle">
        <summary class="cursor-pointer px-4 py-3 font-medium text-gray-900">Edit sections by hand</summary>
        <div class="border-t border-gray-200 p-4">
          <HermesSectionBuilder v-model="sections" :title="template.name" :sample="draftResult ? draftResult.data : null" />
        </div>
      </details>
      <details class="rounded-lg border border-gray-200 bg-white" data-test="advanced">
        <summary class="cursor-pointer px-4 py-3 font-medium text-gray-900">Advanced: JSON</summary>
        <div class="space-y-4 border-t border-gray-200 p-4">
          <p class="text-gray-500">The raw result schema and display the sections are saved as. Edit here only for settings the sections don't cover.</p>
          <label class="block"><span class="mb-2 block text-gray-500">Result schema (JSON)</span><textarea v-model="form.schemaText" rows="12" :class="[INPUT, 'font-mono']" data-test="draft-schema" @change="loadSections" /></label>
          <label class="block"><span class="mb-2 block text-gray-500">Display (JSON)</span><textarea v-model="form.displayText" rows="8" :class="[INPUT, 'font-mono']" data-test="draft-display" @change="loadSections" /></label>
          <p v-if="jsonError" class="rounded bg-neutral-50 px-2 py-1 text-gray-900" data-test="json-error">{{ jsonError }} Your other changes keep saving once it's fixed.</p>
        </div>
      </details>

      <!-- Deleting a draft is deliberately out of the way, and needs its name typed. -->
      <details class="rounded-lg border border-red-200 bg-white" data-test="danger-zone">
        <summary class="cursor-pointer px-4 py-3 font-medium text-gray-900">Danger zone</summary>
        <div class="flex flex-wrap items-end gap-3 border-t border-red-200 p-4">
          <div class="min-w-0 flex-1">
            <div class="font-medium text-gray-900">Delete this draft</div>
            <p class="text-gray-600">Removes the draft and everything in it for good. Live and your other drafts aren’t affected. Type <strong>{{ current.name }}</strong> to confirm.</p>
            <input v-model="deleteConfirm" :class="[INPUT, 'mt-2 max-w-md']" :placeholder="current.name" data-test="delete-confirm-name">
          </div>
          <button type="button" class="rounded-md bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50" :disabled="busy || deleteConfirm.trim() !== current.name" data-test="delete-draft" @click="discard">Delete this draft</button>
        </div>
      </details>
    </template>

    <div v-if="newDraft" class="fixed inset-0 z-20 flex items-center justify-center bg-gray-800/60" role="dialog" aria-modal="true" data-test="new-draft-dialog" @keydown.esc="newDraft = null">
      <div class="w-[600px] rounded-lg border border-gray-200 bg-white">
        <div class="rounded-t-lg bg-primary-100 px-4 py-3 font-semibold text-gray-900">New draft of {{ template.name }}</div>
        <div class="space-y-4 p-4">
          <fieldset>
            <legend class="mb-2 font-medium text-gray-900">Start from</legend>
            <label class="flex items-center gap-2 py-1"><input v-model="newDraft.from" type="radio" value="live" data-test="new-draft-from-live"> What users get now (version {{ template.version }})</label>
            <label v-for="d in drafts" :key="d.id" class="flex items-center gap-2 py-1">
              <input v-model="newDraft.from" type="radio" :value="d.id" :data-test="`new-draft-from-${d.id}`"> A copy of the draft “{{ d.name }}”
            </label>
          </fieldset>
          <label class="block">
            <span class="mb-1 block font-medium text-gray-900">Name</span>
            <input ref="newName" v-model="newDraft.name" :class="INPUT" data-test="new-draft-name" @keydown.enter="create">
          </label>
        </div>
        <div class="flex justify-end gap-3 border-t border-gray-200 px-4 py-3">
          <button type="button" :class="TEXT_BUTTON" @click="newDraft = null">Cancel</button>
          <button type="button" :class="PRIMARY_BUTTON" :disabled="!newDraft.name.trim() || busy" data-test="create-draft" @click="create">Start draft</button>
        </div>
      </div>
    </div>

    <div v-if="compareOpen && draftRun" class="fixed inset-0 z-30 flex flex-col bg-white" role="dialog" aria-modal="true" data-test="comparison" tabindex="-1" @keydown.esc="compareOpen = false">
      <div class="flex items-center gap-3 border-b border-gray-200 px-6 py-3">
        <span class="font-semibold text-gray-900">“{{ draftRun.draft_name }}” compared with live</span>
        <span class="text-gray-500">· engagement {{ draftRun.unit_key }}</span>
        <button type="button" :class="[SECONDARY_BUTTON, 'ml-auto']" data-test="close-comparison" @click="compareOpen = false">Close</button>
      </div>
      <div class="grid flex-1 grid-cols-2 divide-x divide-gray-200 overflow-hidden">
        <div class="overflow-y-auto bg-gray-50 p-6">
          <div class="mb-3 font-medium text-gray-900">Live</div>
          <p v-if="!liveResult" class="text-gray-500">This engagement has no live document yet. Click Summarize on the engagement, then compare again.</p>
          <SummaryDocumentPage v-else :title="template.name" :sections="sectionsOf(liveRun.display || template.display)" :content="liveResult.data || {}" :patient-name="liveResult.entity_name || ''" :generated="absolute(liveResult.created_at)" />
        </div>
        <div class="overflow-y-auto bg-gray-50 p-6">
          <div class="mb-3 font-medium text-gray-900">Your draft</div>
          <p v-if="draftRun.status === 'failed'" class="rounded-md bg-red-tint px-3 py-2 text-gray-900">{{ draftRun.error || 'The draft run failed.' }}</p>
          <p v-else-if="!draftResult" class="text-gray-500">{{ draftLive ? 'Waiting for the result…' : 'No result recorded.' }}</p>
          <SummaryDocumentPage v-else :title="template.name" :sections="sectionsOf(draftRun.display)" :content="draftResult.data || {}" :patient-name="draftResult.entity_name || ''" :generated="absolute(draftResult.created_at)" />
        </div>
      </div>
    </div>

    <div v-if="diff" class="fixed inset-0 z-20 flex items-center justify-center bg-gray-800/60" role="dialog" aria-modal="true" data-test="diff-dialog">
      <div class="flex max-h-[80vh] w-[900px] flex-col rounded-lg border border-gray-200 bg-white">
        <div class="flex items-center rounded-t-lg bg-primary-100 px-4 py-3 font-semibold text-gray-900">
          {{ diff.title }}
          <button type="button" class="ml-auto font-normal text-gray-700" @click="diff = null">Close</button>
        </div>
        <div class="overflow-y-auto p-4">
          <HermesChangeSummary :before="diff.before" :after="diff.after" :title="template.name" :before-label="diff.beforeLabel" :after-label="diff.afterLabel" />
        </div>
      </div>
    </div>

    <div v-if="promote" class="fixed inset-0 z-20 flex items-center justify-center bg-gray-800/60" role="dialog" aria-modal="true" data-test="promote-dialog">
      <div class="w-[640px] rounded-lg border border-gray-200 bg-white">
        <div class="rounded-t-lg bg-primary-100 px-4 py-3 font-semibold text-gray-900">Publish “{{ current.name }}”?</div>
        <div class="space-y-3 p-4 text-gray-900">
          <p><strong>From now on, every {{ template.name }} uses this draft</strong>: when someone clicks Summarize, the program buttons, and schedules. Documents already made don't change.</p>
          <p data-test="version-change">The live document goes from <strong>version {{ promote.from_version }} to version {{ promote.to_version }}</strong>.</p>
          <p v-if="promote.live_changed" class="rounded-md bg-neutral-100 px-3 py-2" data-test="live-changed">
            Live has changed since you started this draft (it was version {{ promote.base_version }}, it's now version {{ promote.from_version }}). Publishing replaces those changes.
          </p>
          <label class="flex items-center gap-2"><input v-model="promoteConfirmed" type="checkbox" data-test="promote-confirm"> I understand this changes what users get.</label>
        </div>
        <div class="flex justify-end gap-3 border-t border-gray-200 px-4 py-3">
          <button type="button" :class="TEXT_BUTTON" @click="promote = null">Cancel</button>
          <button type="button" :class="PRIMARY_BUTTON" :disabled="!promoteConfirmed || busy" data-test="promote-submit" @click="doPromote">Publish</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SummaryDocumentPage from '@/components/Care_Management/Widgets/PatientEngagement/SummaryDocumentPage.vue';
import {
  createDraft, discardDraft, listDraftRuns, listDrafts, liveRunFor, promoteDraft, promotePreview, runDraft, saveDraft,
} from '@/services/api/hermesDrafts';
import HermesAuthoringAssistant from './HermesAuthoringAssistant.vue';
import HermesBadge from './HermesBadge.vue';
import HermesChangeSummary from './HermesChangeSummary.vue';
import HermesSectionBuilder from './HermesSectionBuilder.vue';
import { sampleContent, sectionsFrom, toTemplate } from './documentSections';
import { lintPrompt } from './promptLint';
import {
  API, INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, SIZED_INPUT, TEXT_BUTTON, absolute, errorOf, relative, renderPrompt,
} from './hermes';

const TERMINAL = ['completed', 'failed', 'cancelled'];
const POLL_MS = 3000;
const SAVE_DELAY_MS = 800;
const VIEW_TABS = [
  { key: 'document', label: 'Document' },
  { key: 'instructions', label: 'Instructions' },
  { key: 'try', label: 'Try it on an engagement' },
];

function asText(value) {
  return value && Object.keys(value).length ? JSON.stringify(value, null, 2) : '';
}

// The same content, whatever the key order: Postgres doesn't keep JSON key order, so what comes back from a save can
// be ordered differently from what was sent.
function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map((k) => [k, canonical(value[k])]));
  return value;
}
function sameJson(a, b) {
  const empty = (v) => v === null || v === undefined || (typeof v === 'object' && !Array.isArray(v) && !Object.keys(v).length);
  if (empty(a) && empty(b)) return true;
  return JSON.stringify(canonical(a ?? null)) === JSON.stringify(canonical(b ?? null));
}

export default {
  name: 'HermesTemplateDrafts',
  components: { HermesAuthoringAssistant, HermesBadge, HermesChangeSummary, HermesSectionBuilder, SummaryDocumentPage },
  props: {
    template: { type: Object, required: true },
    // The template's History (newest first), used to diff a draft against the version it was copied from.
    versions: { type: Array, default: () => [] },
  },
  emits: ['promoted'],
  data() {
    return {
      drafts: [], loading: true, current: null, form: { prompt: '', schemaText: '', displayText: '' }, newDraft: null,
      error: '', busy: false, saving: false, saveError: '', saveTimer: null, confirmDiscard: false, deleteConfirm: '', workId: null,
      draftRun: null, draftResult: null, liveRun: null, liveResult: null, timer: null,
      draftRuns: [], compareOpen: false, promote: null, promoteConfirmed: false, view: 'document', promptView: 'read', sections: [], diff: null,
      VIEW_TABS, INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, SIZED_INPUT, TEXT_BUTTON,
    };
  },
  computed: {
    parsed() {
      try {
        return {
          schema: this.form.schemaText.trim() ? JSON.parse(this.form.schemaText) : null,
          display: this.form.displayText.trim() ? JSON.parse(this.form.displayText) : {},
          error: '',
        };
      } catch (e) {
        return { error: `The JSON isn't valid: ${e.message}.` };
      }
    },
    jsonError() {
      return this.parsed.error;
    },
    dirty() {
      if (!this.current || this.parsed.error) return !!this.current && !!this.parsed.error;
      return this.form.prompt !== this.current.prompt
        || !sameJson(this.parsed.schema, this.current.result_schema)
        || !sameJson(this.parsed.display, this.current.display);
    },
    saveStatus() {
      if (this.saveError) return `Couldn't save: ${this.saveError}`;
      if (this.saving || (this.dirty && !this.jsonError)) return 'Saving…';
      if (this.jsonError) return 'Not saved: fix the JSON under Advanced';
      return '✓ Saved';
    },
    // The draft as it is right now, for the AI's Undo check.
    draftNow() {
      if (this.parsed.error) return null;
      return { prompt: this.form.prompt, result_schema: this.parsed.schema, display: this.parsed.display };
    },
    layout() {
      if (this.parsed.error) return { sections: [], content: {} };
      const display = this.parsed.display || {};
      return { sections: display.display_sections || [], content: sampleContent(sectionsFrom(this.parsed.schema, display)) };
    },
    isSuperuser() {
      return !!this.$store?.getters['auth/current_user']?.is_superuser;
    },
    promptNotes() {
      return lintPrompt(this.form.prompt);
    },
    promptHtml() {
      return renderPrompt(this.form.prompt);
    },
    liveMoved() {
      return !!this.current && this.current.base_version !== this.template.version;
    },
    baseVersion() {
      const v = this.current && this.versions.find((x) => x.version === this.current.base_version);
      return v ? { prompt: v.definition.prompt, result_schema: v.definition.result_schema, display: v.display || {} } : null;
    },
    // Anything to discard: the draft differs from the version it started from.
    hasChanges() {
      const base = this.baseVersion;
      if (!base || this.parsed.error) return !!this.parsed.error;
      return this.form.prompt !== base.prompt || !sameJson(this.parsed.schema, base.result_schema) || !sameJson(this.parsed.display, base.display);
    },
    draftLive() {
      return !!this.draftRun && !TERMINAL.includes(this.draftRun.status);
    },
  },
  watch: {
    sections: { deep: true, handler: 'syncFromSections' },
    form: { deep: true, handler: 'scheduleSave' },
    // The open draft lives in the path (/templates/516/drafts/3), so back/forward and links open it.
    '$route.params.draftId': 'openFromRoute',
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
    this.flushSave();
  },
  methods: {
    absolute, relative,
    sectionsOf(display) {
      return (display || {}).display_sections || [];
    },
    async load() {
      try {
        this.drafts = await listDrafts(this.template.id);
        this.openFromRoute();
      } catch (e) {
        this.error = errorOf(e, 'The drafts could not be loaded.');
      } finally {
        this.loading = false;
      }
    },
    syncFromSections() {
      if (this.parsed.error) return;
      const { resultSchema, display } = toTemplate(this.sections, this.parsed.schema, this.parsed.display);
      // Only rewrite the JSON when the sections actually change it, so opening a draft doesn't count as an edit.
      if (!sameJson(resultSchema, this.parsed.schema)) this.form.schemaText = asText(resultSchema);
      if (!sameJson(display, this.parsed.display)) this.form.displayText = asText(display);
    },
    loadSections() {
      if (this.parsed.error) return;
      this.sections = sectionsFrom(this.parsed.schema, this.parsed.display);
    },
    onSectionsToggle(event) {
      if (event.target.open) this.loadSections();
    },
    // Autosave: a draft never affects what users get, so every change is saved as soon as it settles.
    scheduleSave() {
      clearTimeout(this.saveTimer);
      if (!this.current || !this.dirty || this.jsonError) return;
      this.saveTimer = setTimeout(() => this.autosave(), SAVE_DELAY_MS);
    },
    flushSave() {
      clearTimeout(this.saveTimer);
      if (this.current && this.dirty && !this.jsonError && !this.saving) this.autosave();
    },
    async autosave() {
      if (!this.current || !this.dirty || this.jsonError) return;
      if (this.saving) { this.scheduleSave(); return; }
      const id = this.current.id;
      this.saving = true;
      this.saveError = '';
      try {
        const saved = await saveDraft(id, { prompt: this.form.prompt, result_schema: this.parsed.schema, display: this.parsed.display });
        this.drafts = this.drafts.map((d) => (d.id === saved.id ? saved : d));
        if (this.current && this.current.id === saved.id) this.current = saved;
      } catch (e) {
        this.saveError = errorOf(e, 'try again in a moment.');
      } finally {
        this.saving = false;
      }
      this.scheduleSave(); // anything typed while saving
    },
    // The AI's change (or its Undo) replaces the draft; autosave takes it from there.
    replaceDraft({ prompt, result_schema: resultSchema, display }) {
      this.form = { prompt, schemaText: asText(resultSchema), displayText: asText(display) };
      this.loadSections();
    },
    showDiff(which) {
      const draft = { prompt: this.form.prompt, result_schema: this.parsed.schema, display: this.parsed.display };
      const live = { prompt: this.template.prompt, result_schema: this.template.result_schema, display: this.template.display || {} };
      const base = this.baseVersion;
      const from = `Version ${this.current.base_version}`;
      this.diff = which === 'mine'
        ? { title: 'All your changes', before: base, after: draft, beforeLabel: from, afterLabel: 'Your draft' }
        : { title: 'What changed in live since you started', before: base, after: live, beforeLabel: from, afterLabel: `Live now (version ${this.template.version})` };
    },
    routeDraftId() {
      return Number((this.$route && this.$route.params.draftId) || 0);
    },
    openFromRoute() {
      const wanted = this.routeDraftId();
      if (!wanted) {
        // No draft in the path: show the list.
        if (this.current) { this.flushSave(); this.close(); }
        return;
      }
      if (this.current && this.current.id === wanted) return;
      const draft = this.drafts.find((d) => d.id === wanted);
      if (draft) this.open(draft);
      else this.close();
    },
    setRouteDraft(id) {
      if (!this.$router || this.routeDraftId() === (id || 0)) return;
      const { draftId, ...params } = this.$route.params; // eslint-disable-line no-unused-vars
      this.$router.push({ params: id ? { ...params, section: 'drafts', draftId: String(id) } : params });
    },
    backToList() {
      this.flushSave();
      this.close();
      this.setRouteDraft(null);
    },
    startNew() {
      this.newDraft = { from: 'live', name: `Draft ${this.drafts.length + 1}` };
      this.$nextTick(() => this.$refs.newName && this.$refs.newName.select && this.$refs.newName.select());
    },
    close() {
      clearTimeout(this.timer);
      this.confirmDiscard = false;
      this.deleteConfirm = '';
      this.current = null;
      this.compareOpen = false;
      this.draftRun = null;
      this.draftRuns = [];
    },
    open(draft) {
      this.flushSave();
      this.setRouteDraft(draft.id);
      this.current = draft;
      this.form = { prompt: draft.prompt, schemaText: asText(draft.result_schema), displayText: asText(draft.display) };
      this.view = 'document';
      this.promptView = 'read';
      this.loadSections();
      this.error = '';
      this.saveError = '';
      this.confirmDiscard = false;
      this.deleteConfirm = '';
      this.draftRun = null;
      this.draftResult = null;
      this.compareOpen = false;
      this.draftRuns = [];
      this.loadDraftRuns();
    },
    async act(fn, fallback) {
      this.busy = true;
      this.error = '';
      try {
        return await fn();
      } catch (e) {
        this.error = errorOf(e, fallback);
        return null;
      } finally {
        this.busy = false;
      }
    },
    async create() {
      const { name, from } = this.newDraft;
      if (!name.trim()) return;
      const source = from === 'live' ? null : this.drafts.find((d) => d.id === from);
      const body = { template: this.template.id, name: name.trim() };
      if (source) Object.assign(body, { prompt: source.prompt, result_schema: source.result_schema, display: source.display });
      const draft = await this.act(() => createDraft(body), 'The draft could not be created.');
      if (!draft) return;
      this.newDraft = null;
      this.drafts = [draft, ...this.drafts];
      this.open(draft);
    },
    // Put the draft back to the version it started from; autosave stores it. The draft itself stays.
    discardChanges() {
      const base = this.baseVersion;
      this.confirmDiscard = false;
      this.deleteConfirm = '';
      if (!base) return;
      this.replaceDraft(base);
    },
    async discard() {
      const id = this.current.id;
      clearTimeout(this.saveTimer);
      const ok = await this.act(async () => { await discardDraft(id); return true; }, 'The draft could not be deleted.');
      if (!ok) return;
      this.drafts = this.drafts.filter((d) => d.id !== id);
      this.close();
      this.setRouteDraft(null);
    },
    setWorkId(event) {
      const digits = event.target.value.replace(/\D/g, '');
      event.target.value = digits;
      this.workId = digits ? Number(digits) : null;
    },
    async run() {
      const started = await this.act(() => runDraft(this.current.id, { work_id: this.workId }), 'The draft could not be run.');
      if (!started) return;
      this.draftRun = started;
      this.draftResult = null;
      this.loadDraftRuns();
      this.poll();
    },
    async poll() {
      clearTimeout(this.timer);
      try {
        this.draftRun = (await this.$axios.get(`${API}/runs/${this.draftRun.id}/`)).data;
        if (!this.draftLive) {
          this.draftResult = await this.firstResult(this.draftRun.id);
          this.loadDraftRuns();
          return;
        }
      } catch (e) {
        this.error = errorOf(e, 'The draft run could not be loaded.');
        return;
      }
      this.timer = setTimeout(() => this.poll(), POLL_MS);
    },
    async loadDraftRuns() {
      if (!this.current) return;
      try {
        this.draftRuns = await listDraftRuns(this.current.id);
      } catch {
        this.draftRuns = [];
      }
    },
    async showRun(run) {
      clearTimeout(this.timer);
      this.compareOpen = true;
      this.draftRun = run;
      this.$nextTick(() => {
        const overlay = this.$el.querySelector && this.$el.querySelector('[data-test="comparison"]');
        if (overlay) overlay.focus();
      });
      this.draftResult = null;
      this.workId = Number(run.unit_key) || null;
      await this.loadLive(run.id);
      if (this.draftLive) this.poll();
      else this.draftResult = await this.firstResult(run.id);
    },
    async firstResult(runId) {
      const { data } = await this.$axios.get(`${API}/runs/${runId}/results/`);
      return (data.results || [])[0] || null;
    },
    async loadLive(draftRunId) {
      this.liveRun = null;
      this.liveResult = null;
      const live = await liveRunFor(draftRunId);
      if (!live) return;
      this.liveRun = live;
      this.liveResult = await this.firstResult(live.id);
    },
    async openPromote() {
      const preview = await this.act(() => promotePreview(this.current.id), 'Publishing could not be prepared.');
      if (!preview) return;
      this.promoteConfirmed = false;
      this.promote = preview;
    },
    async doPromote() {
      const template = await this.act(() => promoteDraft(this.current.id, {
        confirm: true, expected_version: this.promote.from_version,
      }), 'The draft could not be published.');
      this.promote = null;
      if (!template) return;
      this.drafts = this.drafts.filter((d) => d.id !== this.current.id);
      this.close();
      this.setRouteDraft(null);
      this.$emit('promoted', template);
    },
  },
};
</script>
